import type { HourlySlot } from './energyHistory'
import { resolveTariff, type HourlyTariff, type TariffSource } from './tariffs'

// Hour-by-hour battery simulation with SoC carry-over. Three strategies:
// - self-consumption: store solar excess, discharge to cover household need.
// - trading: day-ahead arbitrage on spot prices (charge cheap, then
//   cover the household's draw in expensive hours, export only what still
//   pays past the meter), independent of solar.
// - self-consumption-trading: hybrid; solar first, and trading in whatever
//   room the day's sun will not fill, up to a configurable share. In practice
//   it trades in winter and on grey days and stores sun the rest of the year.
//   One shared inverter.
//
// Round-trip efficiency is applied in energy accounting: AC charge raises SoC
// 1:1; AC discharge is limited by η × usable SoC, and SoC falls by AC/η.
//
// Every kWh is valued on the side of the meter it actually crosses: energy that
// displaces household draw is worth the all-in import price, energy that leaves
// the house earns the export price. See `tariffs.ts` for why that asymmetry
// carries the whole business case.

/** Round-trip efficiency (~15% conversion loss per cycle). */
export const ROUND_TRIP_EFFICIENCY = 0.85
/**
 * Execution haircut on trading profits: hours where the plan cannot be met
 * because load or availability differed from what was assumed. Day-ahead prices
 * are published a day ahead, so this is not a foresight discount. Applied only
 * to a positive trading result — shrinking a loss would not be conservative.
 */
export const TRADING_REALISM_FACTOR = 0.75
/**
 * Default ceiling on how much of the usable capacity trading may occupy. Surplus
 * has first claim on the pack regardless, so a lower ceiling only limits trading
 * on days the sun leaves the battery empty — winter, mostly — and costs money
 * there without protecting anything.
 */
export const DEFAULT_TRADING_SHARE_PERCENT = 100
/** Trading plans at most this many full cycles per day (captures the typical
 * double valley/peak pattern of Dutch day-ahead prices). */
const MAX_TRADING_CYCLES_PER_DAY = 2
/**
 * Margin a planned cycle must clear beyond its own losses before it is worth
 * committing to. A cycle that only just breaks even on paper turns into a real
 * loss whenever the hour executes differently than planned, and it spends a
 * cycle of battery life to do it.
 */
const MIN_TRADE_MARGIN_EUR_PER_KWH = 0.02

/**
 * How far above the buying hour's all-in price a later hour must sit before a
 * cycle between them is worth it. The conversion loss is bought at the full
 * import price, tax and markup included, so the bar rises with the price level:
 * around 7 ct on a cheap winter night, more when the whole day is expensive.
 * The planner trades on exactly this rule; the report uses it to explain days
 * the battery stands still.
 */
export function requiredTradeSpreadEurPerKwh(buyEurPerKwh: number): number {
  return (buyEurPerKwh + MIN_TRADE_MARGIN_EUR_PER_KWH) / ROUND_TRIP_EFFICIENCY - buyEurPerKwh
}

export interface DailyPriceSpread {
  /** Best all-in import price gap from an hour to a later one that day, €/kWh. */
  spreadEurPerKwh: number
  /** What a cycle bought at that pair's buying hour needs, €/kWh. */
  requiredEurPerKwh: number
}

/**
 * Per calendar day (UTC, as the planner groups them): the best spread on offer
 * and the bar it must clear. Only a buy before a later use counts — on a day
 * whose prices fall from morning to night, the dearest and cheapest hours are in
 * the wrong order, and their gap is not something a battery can trade. Times
 * must be in chronological order.
 */
export function dailyPriceSpreads(
  times: string[],
  tariff: HourlyTariff
): Map<string, DailyPriceSpread> {
  const result = new Map<string, DailyPriceSpread>()
  let date = ''
  let cheapestSoFar = Infinity
  let bestMargin = -Infinity
  for (const time of times) {
    const price = tariff.importEurPerKwh(time)
    if (time.slice(0, 10) !== date) {
      date = time.slice(0, 10)
      cheapestSoFar = price
      bestMargin = -Infinity
      result.set(date, { spreadEurPerKwh: 0, requiredEurPerKwh: requiredTradeSpreadEurPerKwh(price) })
      continue
    }
    const spread = price - cheapestSoFar
    const required = requiredTradeSpreadEurPerKwh(cheapestSoFar)
    if (spread - required > bestMargin) {
      bestMargin = spread - required
      result.set(date, { spreadEurPerKwh: Math.max(0, spread), requiredEurPerKwh: required })
    }
    cheapestSoFar = Math.min(cheapestSoFar, price)
  }
  return result
}

export interface HourlySimResult {
  time: string
  socStart: number
  /** AC energy into the battery (Wh). */
  chargeWh: number
  /** AC energy out of the battery (Wh), after conversion losses. */
  dischargeWh: number
  socEnd: number
}

/**
 * Where the savings come from; the parts sum to `savingsEur`.
 * - trading: net result of the day-ahead arbitrage schedule (after realism haircut).
 * - storedSolar: value of using stored solar at expensive hours, above what
 *   feeding it in would have paid.
 * - avoidedReturn: the feed-in value the household keeps by storing energy
 *   instead of returning it to the grid.
 */
export interface SavingsBreakdownEur {
  trading: number
  storedSolar: number
  avoidedReturn: number
}

export interface BatterySimulationResult {
  totalChargedWh: number
  totalDischargedWh: number
  selfConsumptionPercent: number | null
  savingsEur: number | null
  breakdown: SavingsBreakdownEur | null
  /** Approximate full equivalent cycles (discharged AC / usable capacity). */
  fullEquivalentCycles: number
  hourly: HourlySimResult[]
}

export type BatteryStrategy = 'self-consumption' | 'trading' | 'self-consumption-trading'

export interface BatterySimulationParams extends TariffSource {
  hourlySlots: HourlySlot[]
  capacityKwh: number
  inverterKw: number
  /** Reserve floor: the battery never discharges below this SoC. */
  minSocPercent: number
  /** Charge ceiling: the battery never charges above this SoC (100 = no ceiling). */
  maxSocPercent?: number
  initialSocKwh: number
  strategy: BatteryStrategy
  /** Hybrid strategy: most of the usable capacity trading may occupy, in percent.
   * Stored surplus has first claim regardless. */
  tradingSharePercent?: number
}

/** What the trading plan does in one hour, in AC Wh. */
interface TradePlan {
  chargeWh: number
  /** Released to cover the household's own draw. */
  homeWh: number
  /** Released past the meter. */
  exportWh: number
}

/**
 * Max AC Wh that can leave the battery this hour given usable SoC and η.
 * SoC falls by acOut / η.
 */
function maxAcDischargeWh(usableSocWh: number, inverterWh: number, eta: number): number {
  if (usableSocWh <= 0 || eta <= 0) return 0
  return Math.max(0, Math.min(inverterWh, usableSocWh * eta))
}

function splitSelfConsumption(netEur: number, returnValueEur: number): SavingsBreakdownEur {
  const avoidedReturn = Math.max(0, Math.min(netEur, returnValueEur))
  return { trading: 0, storedSolar: Math.max(0, netEur - avoidedReturn), avoidedReturn }
}

/**
 * Plan the trading leg per calendar day from day-ahead prices and the load the
 * pack may serve.
 *
 * A battery behind the meter earns most by covering the household's own draw
 * in expensive hours: that energy is worth the all-in import price, while
 * anything pushed past the meter only earns the export price, which sits tax
 * and markup lower. So the plan is built per kWh rather than per hour. Every
 * release opportunity — what the house draws in an hour at the import price,
 * and what the inverter has left over at the export price — is filled in order
 * of value, each from the cheapest earlier hour that still has inverter power
 * and pack room for it, as long as the pair clears its losses plus a margin.
 * Planning in whole inverter-hours instead dumped full power into one hour, so
 * most of the energy was exported at a loss and almost no cycle survived.
 */
function planTradingSchedule(
  hourlySlots: HourlySlot[],
  tariff: HourlyTariff,
  usableCapacityWh: number,
  inverterWh: number
): Map<string, TradePlan> {
  const plan = new Map<string, TradePlan>()
  if (usableCapacityWh <= 0 || inverterWh <= 0) return plan

  const eta = ROUND_TRIP_EFFICIENCY
  const byDate = new Map<string, HourlySlot[]>()
  for (const slot of hourlySlots) {
    const date = slot.time.slice(0, 10)
    const list = byDate.get(date) ?? []
    list.push(slot)
    byDate.set(date, list)
  }

  for (const [, day] of byDate) {
    const n = day.length
    const buy = day.map((s) => tariff.importEurPerKwh(s.time))
    const chargeWh = new Array<number>(n).fill(0)
    const homeWh = new Array<number>(n).fill(0)
    const exportWh = new Array<number>(n).fill(0)
    // Energy the plan holds in the pack at the end of each hour (DC, above the floor).
    const storedWh = new Array<number>(n).fill(0)
    const cheapestFirst = [...day.keys()].sort((a, b) => buy[a]! - buy[b]!)
    let releaseBudgetWh = MAX_TRADING_CYCLES_PER_DAY * usableCapacityWh * eta

    const opportunities: { hour: number; value: number; wh: number; toHome: boolean }[] = []
    day.forEach((slot, hour) => {
      const needWh = Math.max(0, slot.delivered - slot.returned)
      if (needWh > 0) opportunities.push({ hour, value: buy[hour]!, wh: needWh, toHome: true })
      opportunities.push({
        hour,
        value: tariff.exportEurPerKwh(slot.time),
        wh: inverterWh,
        toHome: false,
      })
    })
    opportunities.sort((a, b) => b.value - a.value)

    for (const opp of opportunities) {
      const i = opp.hour
      let wantWh = opp.wh
      while (wantWh > 1e-6 && releaseBudgetWh > 1e-6 && chargeWh[i] === 0) {
        const powerLeftWh = inverterWh - homeWh[i]! - exportWh[i]!
        if (powerLeftWh <= 1e-6) break
        // The cheapest earlier hour with inverter power left and room in the
        // pack all the way from that hour to this one.
        let source = -1
        let roomWh = 0
        for (const c of cheapestFirst) {
          if (c >= i || homeWh[c]! + exportWh[c]! > 0 || chargeWh[c]! >= inverterWh - 1e-6) continue
          let peakWh = 0
          for (let t = c; t < i; t++) peakWh = Math.max(peakWh, storedWh[t]!)
          if (peakWh >= usableCapacityWh - 1e-6) continue
          source = c
          roomWh = usableCapacityWh - peakWh
          break
        }
        if (source < 0) break
        if (opp.value - buy[source]! < requiredTradeSpreadEurPerKwh(buy[source]!)) break

        const outWh = Math.min(
          wantWh,
          powerLeftWh,
          (inverterWh - chargeWh[source]!) * eta,
          roomWh * eta,
          releaseBudgetWh
        )
        if (outWh <= 1e-6) break
        chargeWh[source]! += outWh / eta
        for (let t = source; t < i; t++) storedWh[t]! += outWh / eta
        if (opp.toHome) homeWh[i]! += outWh
        else exportWh[i]! += outWh
        wantWh -= outWh
        releaseBudgetWh -= outWh
      }
    }

    day.forEach((slot, hour) => {
      if (chargeWh[hour]! + homeWh[hour]! + exportWh[hour]! > 0) {
        plan.set(slot.time, {
          chargeWh: chargeWh[hour]!,
          homeWh: homeWh[hour]!,
          exportWh: exportWh[hour]!,
        })
      }
    })
  }

  return plan
}

/** Conservatism must not flatter a loss, so the haircut only trims a profit. */
function haircutTradingResult(netEur: number): number {
  return netEur > 0 ? netEur * TRADING_REALISM_FACTOR : netEur
}

function fullCycles(totalDischargedWh: number, usableWh: number): number {
  return usableWh > 0 ? totalDischargedWh / usableWh : 0
}

/**
 * Pure arbitrage: buy in the planned hours, then release what was planned —
 * first into the household's own draw, any rest past the meter. Trading € is
 * haircut by TRADING_REALISM_FACTOR.
 */
function simulateTrading(
  hourlySlots: HourlySlot[],
  tariff: HourlyTariff | null,
  maxSocWh: number,
  inverterWh: number,
  minSocWh: number,
  initialSocWh: number
): BatterySimulationResult {
  const eta = ROUND_TRIP_EFFICIENCY
  const usableWh = maxSocWh - minSocWh
  const schedule = tariff
    ? planTradingSchedule(hourlySlots, tariff, usableWh, inverterWh)
    : new Map<string, TradePlan>()

  const hourly: HourlySimResult[] = []
  let totalChargedWh = 0
  let totalDischargedWh = 0
  let savingsEur = 0
  let soc = Math.max(minSocWh, Math.min(initialSocWh, maxSocWh))

  for (const slot of hourlySlots) {
    const socStart = soc
    const planned = schedule.get(slot.time)
    const needWh = Math.max(0, slot.delivered - slot.returned)
    const chargeWh = Math.min(planned?.chargeWh ?? 0, inverterWh, maxSocWh - soc)
    soc += chargeWh
    const toHomeWh = Math.min(
      planned?.homeWh ?? 0,
      needWh,
      maxAcDischargeWh(soc - minSocWh, inverterWh, eta)
    )
    soc -= toHomeWh / eta
    const toGridWh = Math.min(
      planned?.exportWh ?? 0,
      maxAcDischargeWh(soc - minSocWh, inverterWh - toHomeWh, eta)
    )
    soc -= toGridWh / eta
    soc = Math.max(minSocWh, Math.min(maxSocWh, soc))
    const dischargeWh = toHomeWh + toGridWh
    totalChargedWh += chargeWh
    totalDischargedWh += dischargeWh
    if (tariff) {
      // Energy into the house saves an import at the all-in price, energy past
      // the meter earns the export price; what filled the battery came off the
      // grid at the full import price.
      savingsEur +=
        (toHomeWh / 1000) * tariff.importEurPerKwh(slot.time) +
        (toGridWh / 1000) * tariff.exportEurPerKwh(slot.time) -
        (chargeWh / 1000) * tariff.importEurPerKwh(slot.time)
    }
    hourly.push({ time: slot.time, socStart, chargeWh, dischargeWh, socEnd: soc })
  }

  const tradingEur = haircutTradingResult(savingsEur)
  return {
    totalChargedWh,
    totalDischargedWh,
    selfConsumptionPercent: null,
    savingsEur: tariff ? tradingEur : null,
    breakdown: tariff ? { trading: tradingEur, storedSolar: 0, avoidedReturn: 0 } : null,
    fullEquivalentCycles: fullCycles(totalDischargedWh, usableWh),
    hourly,
  }
}

/**
 * What the load looks like once stored surplus has had its turn at it. The pack
 * serves the house from solar first, so the need the trading leg can still sell
 * into is smaller than the meter suggests; planning against the raw load buys
 * energy that ends up exported at a loss.
 */
function loadBeyondStoredSolar(
  hourlySlots: HourlySlot[],
  usableWh: number,
  inverterWh: number,
  eta: number
): HourlySlot[] {
  let soc = 0
  return hourlySlots.map((slot) => {
    const excessWh = Math.max(0, slot.returned - slot.delivered)
    const needWh = Math.max(0, slot.delivered - slot.returned)
    const chargeWh = Math.min(inverterWh, usableWh - soc, excessWh)
    const dischargeWh = Math.min(maxAcDischargeWh(soc, inverterWh, eta), needWh)
    soc += chargeWh - dischargeWh / eta
    return { ...slot, delivered: Math.max(0, slot.delivered - dischargeWh) }
  })
}

/**
 * How much surplus is still to come later on the same calendar day, per hour.
 * A controller with a solar forecast knows this, and it is what lets it refuse a
 * cheap night hour that would fill the pack before the sun gets a chance.
 */
function surplusStillToComeWh(hourlySlots: HourlySlot[]): number[] {
  const remaining = new Array<number>(hourlySlots.length).fill(0)
  let runningDay = ''
  let runningWh = 0
  for (let i = hourlySlots.length - 1; i >= 0; i--) {
    const slot = hourlySlots[i]!
    const day = slot.time.slice(0, 10)
    if (day !== runningDay) {
      runningDay = day
      runningWh = 0
    }
    remaining[i] = runningWh
    runningWh += Math.max(0, slot.returned - slot.delivered)
  }
  return remaining
}

/**
 * Hybrid: one pack, solar first. Surplus may fill the whole battery; trading is
 * allowed the share the user picked, but only into room today's remaining sun
 * will not reach. Splitting the pack into fixed compartments instead made a
 * higher trading share strictly worse on solar households — it reserved space
 * away from storing surplus, which earns roughly three times what grid arbitrage
 * does because its input costs the export price rather than the import price.
 * Energy in a real pack has no label, but the controller decides when to
 * release it, and that is what the two pools model: stored sun may cover the
 * house in any hour, while energy bought from the grid is only released in the
 * hours the plan bought it for. Letting bought energy drain into whatever hour
 * came next spent it on cheap hours and left the expensive ones short, which
 * made the hybrid earn less than pure trading on a house without solar. Trading
 * € is haircut; stored solar is not.
 */
function simulateHybrid(
  hourlySlots: HourlySlot[],
  tariff: HourlyTariff | null,
  maxSocWh: number,
  inverterWh: number,
  minSocWh: number,
  initialSocWh: number,
  tradingSharePercent: number
): BatterySimulationResult {
  const eta = ROUND_TRIP_EFFICIENCY
  const usableWh = Math.max(0, maxSocWh - minSocWh)
  const share = Math.max(0, Math.min(100, tradingSharePercent)) / 100
  const tradingCapWh = usableWh * share

  const schedule = tariff
    ? planTradingSchedule(
        loadBeyondStoredSolar(hourlySlots, usableWh, inverterWh, eta),
        tariff,
        tradingCapWh,
        inverterWh
      )
    : new Map<string, TradePlan>()
  const reservedForSunWh = surplusStillToComeWh(hourlySlots)

  const initialUsableWh = Math.max(minSocWh, Math.min(initialSocWh, maxSocWh)) - minSocWh
  let solarSoc = initialUsableWh
  let gridSoc = 0

  const hourly: HourlySimResult[] = []
  let totalChargedWh = 0
  let totalDischargedWh = 0
  let totalGridDrawWh = 0
  let totalGridDrawAfterBatteryWh = 0
  let solarValueEur = 0
  let solarCostEur = 0
  let gridValueEur = 0
  let gridCostEur = 0

  hourlySlots.forEach((slot, index) => {
    const socStart = minSocWh + solarSoc + gridSoc
    const excessWh = Math.max(0, slot.returned - slot.delivered)
    const needWh = Math.max(0, slot.delivered - slot.returned)
    totalGridDrawWh += needWh

    // Surplus first, into the whole pack.
    const solarChargeWh = Math.max(
      0,
      Math.min(inverterWh, usableWh - solarSoc - gridSoc, excessWh)
    )
    solarSoc += solarChargeWh

    // Then buy, if this hour was planned for it and the sun has no claim on the room.
    const planned = schedule.get(slot.time)
    let gridChargeWh = 0
    if (planned && planned.chargeWh > 0) {
      const keepFreeWh = Math.min(usableWh, reservedForSunWh[index] ?? 0)
      gridChargeWh = Math.max(
        0,
        Math.min(
          planned.chargeWh,
          inverterWh - solarChargeWh,
          tradingCapWh - gridSoc,
          usableWh - solarSoc - gridSoc - keepFreeWh
        )
      )
      gridSoc += gridChargeWh
    }

    // Cover the house before anything leaves it: stored sun in any hour, bought
    // energy only where the plan meant it to go.
    const chargeWh = solarChargeWh + gridChargeWh
    const fromSolarWh = Math.min(maxAcDischargeWh(solarSoc, inverterWh - chargeWh, eta), needWh)
    const fromGridWh = Math.min(
      planned?.homeWh ?? 0,
      needWh - fromSolarWh,
      maxAcDischargeWh(gridSoc, inverterWh - chargeWh - fromSolarWh, eta)
    )
    const toHomeWh = fromSolarWh + fromGridWh
    solarSoc -= fromSolarWh / eta
    gridSoc -= fromGridWh / eta

    // Then sell, but only what was bought to sell. Pushing stored surplus out at
    // the export price throws away the import price the house would have paid for
    // it, which is the larger number in every hour the tax exists.
    const toGridWh = Math.min(
      planned?.exportWh ?? 0,
      maxAcDischargeWh(gridSoc, inverterWh - chargeWh - toHomeWh, eta)
    )
    gridSoc -= toGridWh / eta

    const dischargeWh = toHomeWh + toGridWh
    totalChargedWh += chargeWh
    totalDischargedWh += dischargeWh
    totalGridDrawAfterBatteryWh += needWh - toHomeWh
    if (tariff) {
      const importPrice = tariff.importEurPerKwh(slot.time)
      const exportPrice = tariff.exportEurPerKwh(slot.time)
      // Storing surplus gives up what exporting it would have paid; buying costs
      // the full import price including tax.
      solarCostEur += (solarChargeWh / 1000) * exportPrice
      gridCostEur += (gridChargeWh / 1000) * importPrice
      solarValueEur += (fromSolarWh / 1000) * importPrice
      gridValueEur += (fromGridWh / 1000) * importPrice + (toGridWh / 1000) * exportPrice
    }

    hourly.push({
      time: slot.time,
      socStart,
      chargeWh,
      dischargeWh,
      socEnd: minSocWh + solarSoc + gridSoc,
    })
  })

  const scNetEur = solarValueEur - solarCostEur
  const scReturnValueEur = solarCostEur
  const tradeNetEur = gridValueEur - gridCostEur

  const selfConsumptionPercent =
    totalGridDrawWh > 0 ? Math.round((1 - totalGridDrawAfterBatteryWh / totalGridDrawWh) * 100) : 0

  const scSplit = splitSelfConsumption(scNetEur, scReturnValueEur)
  const tradingEur = haircutTradingResult(tradeNetEur)
  const savingsEur = scSplit.storedSolar + scSplit.avoidedReturn + tradingEur
  return {
    totalChargedWh,
    totalDischargedWh,
    selfConsumptionPercent,
    savingsEur: tariff ? savingsEur : null,
    breakdown: tariff
      ? { trading: tradingEur, storedSolar: scSplit.storedSolar, avoidedReturn: scSplit.avoidedReturn }
      : null,
    fullEquivalentCycles: fullCycles(totalDischargedWh, usableWh),
    hourly,
  }
}

/**
 * Simulate a battery over the given hourly slots with SoC carry-over and
 * round-trip efficiency. Charge/discharge are limited by inverter power
 * (kW == kWh per hourly slot).
 */
export function runBatterySimulation(params: BatterySimulationParams): BatterySimulationResult {
  const {
    hourlySlots,
    capacityKwh,
    inverterKw,
    minSocPercent,
    maxSocPercent = 100,
    initialSocKwh,
    strategy,
    tradingSharePercent = DEFAULT_TRADING_SHARE_PERCENT,
  } = params

  const eta = ROUND_TRIP_EFFICIENCY
  const tariff = resolveTariff(params)
  const capacityWh = capacityKwh * 1000
  const inverterWh = inverterKw * 1000
  const minSocWh = capacityWh * (minSocPercent / 100)
  const maxSocWh = Math.max(minSocWh, capacityWh * (maxSocPercent / 100))
  const initialSocWh = initialSocKwh * 1000
  const usableWh = maxSocWh - minSocWh

  if (strategy === 'trading') {
    return simulateTrading(hourlySlots, tariff, maxSocWh, inverterWh, minSocWh, initialSocWh)
  }
  if (strategy === 'self-consumption-trading') {
    return simulateHybrid(
      hourlySlots,
      tariff,
      maxSocWh,
      inverterWh,
      minSocWh,
      initialSocWh,
      tradingSharePercent
    )
  }

  // Self-consumption: charge from solar excess, discharge to cover grid draw.
  const hourly: HourlySimResult[] = []
  let totalChargedWh = 0
  let totalDischargedWh = 0
  let totalGridDrawWh = 0
  let totalGridDrawAfterBatteryWh = 0

  let soc = Math.min(initialSocWh, maxSocWh)
  soc = Math.max(soc, minSocWh)

  for (const slot of hourlySlots) {
    const socStart = soc
    const excessWh = Math.max(0, slot.returned - slot.delivered)
    const needWh = Math.max(0, slot.delivered - slot.returned)
    totalGridDrawWh += needWh

    const headroom = maxSocWh - soc
    const chargeWh = Math.min(inverterWh, headroom, excessWh)
    const dischargeWh = Math.min(maxAcDischargeWh(soc - minSocWh, inverterWh, eta), needWh)

    soc = Math.max(minSocWh, Math.min(maxSocWh, soc + chargeWh - dischargeWh / eta))
    totalChargedWh += chargeWh
    totalDischargedWh += dischargeWh
    totalGridDrawAfterBatteryWh += needWh - dischargeWh

    hourly.push({ time: slot.time, socStart, chargeWh, dischargeWh, socEnd: soc })
  }

  const selfConsumptionPercent =
    totalGridDrawWh > 0 ? Math.round((1 - totalGridDrawAfterBatteryWh / totalGridDrawWh) * 100) : 0

  let savingsEur: number | null = null
  let breakdown: SavingsBreakdownEur | null = null
  if (tariff) {
    // Discharge displaces an import at the all-in price; charging gives up what
    // exporting that surplus would have paid, which a feed-in fee can turn into
    // a saving of its own.
    const dischargeValue = hourly.reduce(
      (sum, hr) => sum + (hr.dischargeWh / 1000) * tariff.importEurPerKwh(hr.time),
      0
    )
    const returnValueEur = hourly.reduce(
      (sum, hr) => sum + (hr.chargeWh / 1000) * tariff.exportEurPerKwh(hr.time),
      0
    )
    savingsEur = Math.max(0, dischargeValue - returnValueEur)
    breakdown = splitSelfConsumption(savingsEur, returnValueEur)
  }

  return {
    totalChargedWh,
    totalDischargedWh,
    selfConsumptionPercent,
    savingsEur,
    breakdown,
    fullEquivalentCycles: fullCycles(totalDischargedWh, usableWh),
    hourly,
  }
}
